











































import { defineComponent, useRoute, ref, useFetch, watch, computed } from '@nuxtjs/composition-api';
import { uniqueId } from 'lodash-es';
import ProductReviewCardImage from '~/components/pages/Product/ProductReviewCardImage.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppLoader from '~/components/partials/App/AppLoader.vue';
import AppSeeMoreButton from '~/components/partials/App/AppSeeMoreButton.vue';
import AppSortSelect from '~/components/partials/App/AppSortSelect.vue';
import GlobalHeaderMenu from '~/components/partials/Global/GlobalHeaderMenu.vue';
import LayoutDefault from '~/components/partials/Layout/LayoutDefault.vue';
import { useComment } from '~/composables/mypage/useComment';
import { useLayoutProps } from '~/composables/useLayoutProps';
import { useStore } from '~/composables/useStore';
import { COMMENT_SORT_OPTIONS, COMMENT_PAGES } from '~/constants/comments';
import { ECSchema } from '~/types/api';

export default defineComponent({
  components: {
    LayoutDefault,
    ProductReviewCardImage,
    AppSeeMoreButton,
    AppSortSelect,
    AppLoader,
    AppIcon,
    GlobalHeaderMenu,
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    // commentId が数字ではない場合、undefined として設定する
    const commentId = computed(() =>
      isNaN(+route.value.params.commentId) ? undefined : +route.value.params.commentId
    );

    const commentList = ref<ECSchema<'review.UserReview'>[]>([]);
    const totalComments = ref(0);
    const hasMoreComments = ref(false);
    const nickname = ref('');
    const isLoading = ref(true);
    let currentPage = 1;
    const { currentSortIndex, currentSortParameter, likedComments, fetchLikedComments, handleLikeChange } = useComment(
      COMMENT_PAGES.USER
    );

    useFetch(async () => {
      try {
        if (commentId.value === undefined) return;
        const comments = await store.dispatch('modules/reviews/fetchCommentsByCommentId', {
          path: { review_id: commentId.value },
          query: {
            limit: 10,
            page: currentPage,
            sort: currentSortParameter.value,
            display_flag: true,
            status: 'approved',
          },
        });
        commentList.value = comments.data;
        totalComments.value = comments.total || 0;
        nickname.value = comments.display_nickname;
        hasMoreComments.value = commentList.value.length < totalComments.value;
        await fetchLikedComments();
      } catch {
        // 何もしない
      } finally {
        isLoading.value = false;
      }
    });

    const loadAdditionalComments = async () => {
      // 十個ずつしか取得できないので、表示中の最後のコメント情報もAPIに送らなければならない
      if (commentId.value === undefined || commentList.value.length === 0) return;
      currentPage++;
      const additionalComments = await store.dispatch('modules/reviews/fetchCommentsByCommentId', {
        path: { review_id: commentId.value },
        query: {
          limit: 10,
          page: currentPage,
          sort: currentSortParameter.value,
          display_flag: true,
          status: 'approved',
        },
      });
      if (additionalComments.data) commentList.value = commentList.value.concat(additionalComments.data);
      const currentLength = commentList.value.length;
      if (currentLength === additionalComments.total) hasMoreComments.value = false;
    };

    const fetchComments = async () => {
      if (commentId.value === undefined) return;
      // いいねボタンの状態を更新する必要がある
      await fetchLikedComments();
      const userReviews = await store.dispatch('modules/reviews/fetchCommentsByCommentId', {
        path: { review_id: commentId.value },
        query: {
          limit: commentList.value.length,
          page: 1,
          sort: currentSortParameter.value,
          display_flag: true,
          status: 'approved',
        },
      });
      commentList.value = userReviews.data;
    };

    watch(currentSortIndex, () => {
      store.commit('modules/reviews/setUserCommentsSortIndex', currentSortIndex.value);
      fetchComments();
    });

    useLayoutProps(() => ({
      layout: {
        header: 'always',
        footer: 'always',
        footerMenu: 'always',
        headerMenu: 'never',
        topBanner: 'never',
      },
    }));

    return {
      COMMENT_SORT_OPTIONS,
      commentList,
      totalComments,
      hasMoreComments,
      nickname,
      likedComments,
      handleLikeChange,
      isLoading,
      currentSortIndex,
      uniqueId,
      loadAdditionalComments,
    };
  },
});
